import { serializeDateFields } from 'common/utils/time';
import { PaginatedApiPayloadV2 } from 'domain/Common';
import { communityIdFromApi } from 'domain/CommunityId';
import {
  EventDetails,
  EventDetailsApiResponse,
  EventHostApiResponse,
  EventHostType,
  EventListItem,
  EventListItemApiResponse,
  PublicEventDetails,
  PublicEventDetailsApiResponse,
  SimilarEvent,
  SimilarEventApiResponse,
  SimilarEventQuestion,
  SimilarEventQuestionApiResponse,
  TranscriptionMode,
} from 'domain/event';
import { EventSeries, EventSeriesApiResponse } from 'domain/event/EventSeries';
import { DEFAULT_NATTERING_DURATION } from 'event/constants';
import { pick } from 'lodash-es';
import flow from 'lodash/fp/flow';
import { transformPaginatedApiPayload } from 'modules/api/utils';

import {
  mapEventRecordingFromApi,
  mapEventRelatedVideoFromApi,
  mapEventTimeFromApi,
  mapOwnerBaseFromApi,
} from './common';
import { mapEventRelatedImagesToEventImage, mapEventRelatedImagesToSponsors } from './eventImage';

export const mapPublicEventFromApi = (data: PublicEventDetailsApiResponse): PublicEventDetails => ({
  // Backend already sends the UUID publicId here; keep it a string.
  id: String(data.id),
  communityId: communityIdFromApi(data.communityId),
  communitySlug: data.communitySlug,
  communityName: data.communityName,
  hash: data.hash,
  name: data.name,
  description: data.description,
  brandingColor: data.brandingColor,
  logoImageLink: data.logoImageLink,
  status: data.state,
  owner: mapOwnerBaseFromApi(data),
  eventImage: mapEventRelatedImagesToEventImage(data.eventRelatedImages),
  sponsors: mapEventRelatedImagesToSponsors(data.eventRelatedImages),
  // The public register response no longer carries a numeric id; `EventTime.id` is unused, so pass 0.
  eventTime: mapEventTimeFromApi({ id: 0, startTime: data.startTime, durationInMinutes: data.durationMinutes }),
  isPinEnabled: data.pinEnabled,
});

const mapEventListItemFromApi = (data: EventListItemApiResponse): EventListItem => ({
  ...pick(data, [
    'id',
    'publicId',
    'hash',
    'name',
    'description',
    'state',
    'deletionDate',
    'agoraProxyMode',
    'pin',
    'hosts',
  ]),
  communityId: communityIdFromApi(data.communityId),
  isOwned: data.owned,
  owner: mapOwnerBaseFromApi(data),
  status: data.state,
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startDate, durationInMinutes: data.eventDuration }),
  eventImage: mapEventRelatedImagesToEventImage(data.eventRelatedImages),
  eventRelatedVideos: data.eventRelatedVideos.map(mapEventRelatedVideoFromApi),
  guestCount: data.eventGuestCount,
  isPinEnabled: data.isPinEnabled,
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
  seriesId: data.seriesId ?? undefined,
});

export const mapPaginatedEventListItemsFromApi = flow(
  transformPaginatedApiPayload<EventListItemApiResponse>,
  (payload) => ({
    ...payload,
    data: payload.data.map(flow(serializeDateFields('startDate'), mapEventListItemFromApi)),
  })
);

export const mapEventDetailsFromApi = (data: EventDetailsApiResponse): EventDetails => ({
  ...pick(data, [
    'id',
    'publicId',
    'owner',
    'hash',
    'name',
    'description',
    'deletionDate',
    'agoraProxyMode',
    'pin',
    'hasCompletedPreEventQuestions',
    'hasCompletedPostEventQuestions',
    'isUserRegistered',
  ]),
  communityId: communityIdFromApi(data.communityId),
  isOwned: data.owned,
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startDate, durationInMinutes: data.eventDuration }),
  status: data.state,
  eventImage: mapEventRelatedImagesToEventImage(data.eventRelatedImages),
  sponsors: mapEventRelatedImagesToSponsors(data.eventRelatedImages),
  eventRelatedVideos: data.eventRelatedVideos.map(mapEventRelatedVideoFromApi),
  eventRecordings: data.eventRecordings.map(mapEventRecordingFromApi),
  isPinEnabled: data.isPinEnabled,
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
  roleFieldDisabled: data.roleFieldDisabled ?? false,
  hosts: data.hosts?.map(mapHostFromApi) ?? [],
  seriesId: data.seriesId ?? undefined,
  defaultPace: data.defaultPace ?? DEFAULT_NATTERING_DURATION,
  defaultTranscriptionMode: data.defaultTranscriptionMode ?? TranscriptionMode.OPTIONAL,
  // Deliberately NOT defaulted: `undefined` means the backend has no answer (pre-HNS-372 deploy),
  // and only the wizard knows the community default to fall back to. A `?? true` here would seed
  // the toggle with a value the backend never sent.
  reportQuotesEnabled: data.reportQuotesEnabled,
  reportQuotesEditable: data.reportQuotesEditable ?? false,
  // isSingleSelection is always true: the tag service has no multi-select concept, so every category
  // there is single-select and the flag is not on the wire.
  tagCategories: (data.tagCategories ?? []).map(({ name, tags }) => ({ name, tags, isSingleSelection: true })),
});

export const mapEventSeriesDetailsFromApi = (data: EventSeriesApiResponse): EventSeries => ({
  id: data.id,
  status: data.state,
  communityId: communityIdFromApi(data.communityId),
  name: data.name,
  description: data.description,
  eventImage: mapEventRelatedImagesToEventImage(data.relatedImages),
  events: data.events.map((event) => ({ ...event, startTime: event.startTime * 1000 })),
  hosts: data.hosts.map(mapHostFromApi),
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
  isPinEnabled: data.isPinEnabled,
  relatedVideos: data.relatedVideos.map(mapEventRelatedVideoFromApi),
  sponsors: mapEventRelatedImagesToSponsors(data.relatedImages),
  defaultPace: data.defaultPace ?? DEFAULT_NATTERING_DURATION,
  defaultTranscriptionMode: data.defaultTranscriptionMode ?? TranscriptionMode.OPTIONAL,
  // See `mapEventDetailsFromApi`. The series value is the AND across every occurrence.
  reportQuotesEnabled: data.reportQuotesEnabled,
  reportQuotesEditable: data.reportQuotesEditable ?? false,
});

export const mapPaginatedSimilarEventsFromApi = (
  payload: PaginatedApiPayloadV2<SimilarEventApiResponse>
): PaginatedApiPayloadV2<SimilarEvent> => ({
  ...payload,
  data: payload.data.map(mapSimilarEventsFromApi),
});

const mapSimilarEventsFromApi = (data: SimilarEventApiResponse): SimilarEvent => ({
  ...pick(data, ['id', 'owner', 'communityId', 'hash', 'name', 'description', 'deletionDate', 'agoraProxyMode', 'pin']),
  eventTime: mapEventTimeFromApi({ id: data.id, startTime: data.startDate, durationInMinutes: data.eventDuration }),
  status: data.state,
  questions: data.questions.map(mapSimilarEventQuestionFromApi),
  eventRelatedVideos: data.eventRelatedVideos.map(mapEventRelatedVideoFromApi),
  guestCount: data.eventGuestCount,
  isAgoraCloudRecordingEnabled: data.isAgoraCloudRecordingEnabled,
});

const mapSimilarEventQuestionFromApi = (data: SimilarEventQuestionApiResponse): SimilarEventQuestion => data;

export const mapEventDetailsToEventListItem = (eventDetails: EventDetails): EventListItem => ({
  ...eventDetails,
  guestCount: 0,
  hosts: eventDetails.hosts.map(({ accountId }) => accountId),
});

export const mapHostFromApi = (host: EventHostApiResponse): EventHostType => ({
  accountId: host.accountId,
  communityRole: host.communityRole,
  email: host.email,
  avatarLink: host.avatarLink,
  role: host.role,
  name: host.fullName,
});
